import axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Header, Tab } from 'semantic-ui-react'
import BurstWarningBanner from './components/BurstWarningBanner'
import DataStatusIndicator from './components/DataStatusIndicator'
import FluxLiveChart from './components/FluxLiveChart'
import { useBurstStatus } from './hooks/useBurstStatus'
import { useDataStatus } from './hooks/useDataStatus'
import { setFluxLive, setFluxNoData } from '../../../../store/fluxLiveSlice'
import { useAppDispatch } from '../../../../store/hooks'
import { serverUrl } from '../../../../utilites/config'
import { reconnectingSocket } from '../../../../utilites/socket'

const formatUtcForFilename = () => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}_${pad(d.getUTCDate())}-${pad(
    d.getUTCMonth() + 1,
  )}-${d.getUTCFullYear()}`
}

function SolarFluxLive() {
  const connectionRef = useRef<any>(null)
  const [dataX, setDataX] = useState<Date[]>([])
  const [dataY, setDataY] = useState<number[]>([])
  const [activeTab, setActiveTab] = useState(0)
  const [hasActiveSession, setHasActiveSession] = useState<boolean>(true)
  const burstStatus = useBurstStatus(activeTab, 'SolarFluxLive')
  const dispatch = useAppDispatch()

  const bandLabel = activeTab === 0 ? 'F10.7' : 'F30'
  const downloadFilename = `[FLUX][${bandLabel}][1s]_${formatUtcForFilename()}`

  // Get data status for indicator
  const { flux: fluxStatus } = useDataStatus(burstStatus.isActive)
  const currentDateLabel = useMemo(() => {
    const d = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`
  }, [])

  // Memoize axis configs to prevent unnecessary re-renders that reset pan/zoom
  const xaxisConfig = useMemo(
    () => ({ title: { text: `UTC — ${currentDateLabel}` } }),
    [currentDateLabel]
  )

  const yaxisConfig = useMemo(
    () => ({ title: { text: 'SFU' } }),
    []
  )

  // Memoize the chart data to prevent unnecessary re-renders
  const chartData = useMemo(
    () => [
      {
        x: dataX,
        y: dataY,
        mode: 'lines',
        line: { color: 'black', width: 1 },
      },
    ],
    [dataX, dataY],
  )

  useEffect(() => {
    // Track if this effect is still active (not cleaned up)
    let isActive = true

    const saveSocketData = d => {
      if (!isActive) return // Ignore if effect was cleaned up

      if (d.body !== 'NO_DATA') {
        const newx = JSON.parse(d.body)
        setDataX(prevData => [...prevData.slice(1), new Date(new Date(newx[newx.length - 1].date).getTime())])
        setDataY(prevData => [...prevData.slice(1), parseFloat(newx[newx.length - 1].value)])
        setHasActiveSession(true)
        dispatch(setFluxLive())
      } else {
        setHasActiveSession(false)
        dispatch(setFluxNoData())
      }
    }

    axios.get(serverUrl + `/flux/historicalData${activeTab === 0 ? '' : '?band=30'}`).then(data => {
      if (!isActive) return // Ignore if tab changed while fetching

      const newY: number[] = []
      const newX: Date[] = []
      data.data.forEach(item => {
        newY.push(item.value)
        newX.push(new Date(item.date))
      })
      setDataX(newX)
      setDataY(newY)
      if (newX.length > 0) {
        setHasActiveSession(true)
      } else {
        setHasActiveSession(false)
      }
    })

    connectionRef.current = reconnectingSocket(
      serverUrl + '/websocket',
      saveSocketData,
      `/topic/flux-live${activeTab === 0 ? '' : '-30'}`,
      window.location.href,
    )

    return () => {
      isActive = false // Mark as inactive before cleanup
      const conn = connectionRef.current
      if (conn) {
        conn.disconnect?.()
      }
      connectionRef.current = null
      setDataX([])
      setDataY([])
    }
  }, [activeTab, dispatch])

  const panes = [
    {
      menuItem: 'F10.7',
      render: () => (
        <FluxLiveChart
          data={chartData}
          xaxis={xaxisConfig}
          yaxis={yaxisConfig}
          downloadFilename={downloadFilename}
          hasActiveSession={hasActiveSession}
          uiRevisionKey='flux-f10-chart'
        />
      ),
    },
    {
      menuItem: 'F30',
      render: () => (
        <FluxLiveChart
          data={chartData}
          xaxis={xaxisConfig}
          yaxis={yaxisConfig}
          downloadFilename={downloadFilename}
          hasActiveSession={hasActiveSession}
          uiRevisionKey='flux-f30-chart'
        />
      ),
    },
  ]

  // >>> FINALNY RETURN <<<
  return (
    <div className='fade-in'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Header as='h1' style={{ marginBottom: 0 }}>
          Solar Flux
          <Header.Subheader>
            Real time flux plotting for {bandLabel} band
          </Header.Subheader>
        </Header>
        <DataStatusIndicator label='Flux' status={fluxStatus.status} text={fluxStatus.text} title={fluxStatus.title} />
      </div>

      <BurstWarningBanner status={burstStatus} />

      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        className='secondary_tab'
        activeIndex={activeTab}
        onTabChange={(_, { activeIndex }) => setActiveTab((activeIndex as number) ?? 0)}
      />
    </div>
  )
}

export default SolarFluxLive
